import { Toast as AntdMobileToast } from "antd-mobile";
import { isInFlutterApp, imgUrl } from "../utils";
import { ToastShowProps } from "antd-mobile/es/components/toast/methods";
import React from "react";
import i18n from "src/i18n";

/**
 * Toast 类型定义
 */
export type ToastIcon = 'success' | 'fail' | 'warning' | 'loading' | '';
export type ToastTheme = 'light' | 'dark';

export interface ToastOptions extends Omit<ToastShowProps, 'content' | 'icon'> {
  content?: string; // 只能是字符串
  icon?: ToastIcon;
  duration?: number;
  /** 主题模式：light(黑背景白字) | dark(深灰背景白字) */
  theme?: ToastTheme;
}

/**
 * Dark 主题页面路径配置
 * 在这些页面上，Toast 默认使用 dark 主题
 * 判断标准：页面主背景为黑色(#000)或深灰色(#141414)
 */
const DARK_THEME_PAGES: string[] = [
  // 图片预览
  '/imagePreview',
  // 分享页面
  '/summaryShare',
];

/**
 * 检查当前页面是否为 dark 主题页面
 */
function isDarkThemePage(): boolean {
  const pathname = window.location.pathname.toLowerCase();
  return DARK_THEME_PAGES.some(page =>
    pathname === page.toLowerCase() ||
    pathname.startsWith(page.toLowerCase() + '/')
  );
}

/**
 * 图标映射（loading 使用纯 CSS 实现）
 */
const ICON_MAP: Record<ToastIcon, string> = {
  success: "/ss/app/image/privateMvp/select04.svg",
  fail: "/ss/app/image/privateMvp/delete03.svg",
  warning: "/ss/app/image/privateMvp/warn01.svg",
  loading: '/ss/app/image/privateMvp/loading01.svg',
  '': '',
};

/**
 * Toast 代理服务
 * 自动根据环境选择使用 antd mobile Toast 或 Flutter Toast
 */
class ToastService {
  private static instance: ToastService;
  private currentAntdToast: { close: () => void } | null = null;
  private currentToastId: number = 0;
  private toastIdCounter: number = 0;

  private constructor() {}

  /**
   * 获取单例实例
   */
  public static getInstance(): ToastService {
    if (!ToastService.instance) {
      ToastService.instance = new ToastService();
    }
    return ToastService.instance;
  }

  /**
   * 创建带图标的 Toast 内容
   */
  private createToastContent(content: React.ReactNode, icon?: ToastIcon): React.ReactNode {
    
    const iconUrl = ICON_MAP[icon || ''];
    
    return (
      <div className="flexMainXYcenter">
        {iconUrl &&
          <img
            src={imgUrl(iconUrl)}
            width={20}
            height={20}
            className={`mr-small ${icon === 'loading' ? 'toast-loading-spinner' : ''}`}
          />
        }
        <span className='heavy-font' style={{ lineHeight: '150%' }}>{content || i18n.t('加载中...')}</span>
      </div>
    );
  }
  
  /**
   * 显示 Toast
   * 会自动关闭之前的 Toast，确保同时只有一个 Toast 显示
   */
  public show(options: ToastOptions | string) {
    // 先清除之前的 Toast，确保单例
    this.clear();

    const toastOptions = typeof options === 'string'
      ? { content: options }
      : { ...options };

    // 生成新的 Toast ID
    const toastId = ++this.toastIdCounter;
    this.currentToastId = toastId;

    // if (isInFlutterApp()) {
    //   const icon = toastOptions.icon || '';
    //   const duration = toastOptions.duration;
    //   const message = toastOptions.content || i18n.t('加载中...');

    //   window.FlutterJSBridge?.uiTool?.toast?.({
    //     icon,
    //     message,
    //     duration
    //   });

    //   // Flutter 环境下 loading 类型需要 H5 端处理定时关闭
    //   // duration 为 0 表示不自动关闭，其他情况（包括 undefined）都需要定时关闭
    //   if (icon === 'loading' && duration !== 0) {
    //     const actualDuration = duration ?? 2000; // 默认 2 秒
    //     setTimeout(() => {
    //       // 只有当前 Toast ID 匹配时才关闭，避免误关新的 Toast
    //       if (this.currentToastId === toastId) {
    //         this.clear();
    //       }
    //     }, actualDuration);
    //   }

    //   return { close: () => this.clearById(toastId) };
    // }

    const { icon, theme, ...restOptions } = toastOptions;

    // 如果没有指定 theme，根据当前页面自动选择
    const finalTheme = theme ?? (isDarkThemePage() ? 'dark' : 'light');

    // 构建 antd mobile Toast 的 content
    const finalContent = this.createToastContent(toastOptions.content, icon);

    this.currentAntdToast = AntdMobileToast.show({
      ...restOptions,
      content: finalContent,
      maskClassName: `toast-theme-${finalTheme}`,
    });
    return { close: () => this.clearById(toastId) };
  }

  /**
   * 根据 ID 清除 Toast（只有 ID 匹配时才清除）
   */
  private clearById(toastId: number) {
    if (this.currentToastId === toastId) {
      this.clear();
    }
  }

  /**
   * 清除当前 Toast
   */
  public clear() {
    // if (isInFlutterApp()) {
    //   window.FlutterJSBridge?.uiTool?.toast?.({ message: '' });
    //   return
    // }
    
    if (this.currentAntdToast) {
      this.currentAntdToast.close();
      this.currentAntdToast = null;
    }
  }
}

// 导出单例实例
export const Toast = ToastService.getInstance();

// 导出类型
export default Toast;
